/* eslint-disable roros/no-raw-html-in-routes */
// reason: pre-existing <button>/<input>/<select> elements that pre-date the LINT-1 (AILLM-3) rule extension. New code must use components/ui/*. Migration tracked separately.
import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { usePageHeader } from '@/hooks/usePageHeader';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { ChangelogList } from '@/components/changelog/ChangelogList';
import { useAuthStore } from '@/stores/authStore';
import {
  Sparkles,
  FileText,
  CheckCircle,
  Users,
  Shield,
  Settings,
  Database,
  Cloud,
  Mail,
  Server,
  Code,
  FolderTree,
  Key,
  Terminal,
  AlertTriangle,
  Cpu,
  HardDrive,
  Globe,
  Lock,
  Clock,
  ChevronDown,
  ChevronRight,
  Upload,
  BarChart3,
  Bell,
  Palette,
  Brain,
} from 'lucide-react';

/* ─── Collapsible section helper ──────────────────── */
function Section({
  id,
  icon,
  title,
  description,
  defaultOpen = false,
  children,
}: {
  id: string;
  icon: React.ReactNode;
  title: string;
  description: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <Card>
      <CardHeader
        className="cursor-pointer select-none"
        onClick={() => setOpen(o => !o)}
      >
        <div className="flex items-center gap-3">
          <div className="flex-shrink-0 w-9 h-9 rounded-lg bg-primary-100 text-primary-600 flex items-center justify-center">
            {icon}
          </div>
          <div className="flex-1 min-w-0">
            <CardTitle className="flex items-center gap-2">
              {title}
              {open ? <ChevronDown className="w-4 h-4 text-muted-foreground" /> : <ChevronRight className="w-4 h-4 text-muted-foreground" />}
            </CardTitle>
            <CardDescription>{description}</CardDescription>
          </div>
        </div>
      </CardHeader>
      {open && <CardContent>{children}</CardContent>}
    </Card>
  );
}

/* ─── Tiny helpers ────────────────────────────────── */
function CodeBlock({ children }: { children: string }) {
  return (
    <pre className="bg-gray-900 text-gray-100 rounded-lg p-4 text-sm overflow-x-auto whitespace-pre">
      <code>{children}</code>
    </pre>
  );
}

function EnvVar({ name, example, description }: { name: string; example?: string; description: string }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-start gap-1 sm:gap-3 py-2 border-b border-border last:border-0">
      <code className="text-xs font-mono bg-muted text-foreground px-2 py-0.5 rounded shrink-0">{name}</code>
      <span className="text-sm text-muted-foreground flex-1">{description}</span>
      {example && <span className="text-xs text-muted-foreground shrink-0">{example}</span>}
    </div>
  );
}

function FeatureItem({ icon, title, description }: { icon: React.ReactNode; title: string; description: string }) {
  return (
    <div className="flex gap-3">
      <div className="flex-shrink-0 w-8 h-8 bg-primary-100 text-primary-600 rounded-lg flex items-center justify-center">
        {icon}
      </div>
      <div>
        <h4 className="text-sm font-medium text-foreground">{title}</h4>
        <p className="text-xs text-muted-foreground">{description}</p>
      </div>
    </div>
  );
}

/* ─── Tab types ──────────────────────────────────── */
type DocTab = 'whats-new' | 'documentation';

/* ─── Main component ──────────────────────────────── */
export function Documentation() {
  const [activeTab, setActiveTab] = useState<DocTab>('whats-new');
  const user = useAuthStore((s) => s.user);
  const isSuperAdmin = !!(user as any)?.isSuperAdmin;
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith('/admin');

  usePageHeader({
    title: 'Documentation',
    breadcrumbs: isAdminRoute
      ? [{ label: 'Admin', path: '/admin/orgs' }]
      : [{ label: 'Organization', path: '/organization' }],
  });

  return (
    <div className="p-4 sm:p-8 max-w-5xl mx-auto">

      {/* Tab navigation */}
      <div className="flex gap-1 mb-6 border-b border-border">
        <button
          onClick={() => setActiveTab('whats-new')}
          className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors -mb-px ${
            activeTab === 'whats-new'
              ? 'border-primary-600 text-primary-600'
              : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
          }`}
        >
          What's New
        </button>
        <button
          onClick={() => setActiveTab('documentation')}
          className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors -mb-px ${
            activeTab === 'documentation'
              ? 'border-primary-600 text-primary-600'
              : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
          }`}
        >
          Documentation
        </button>
      </div>

      {/* Tab content */}
      {activeTab === 'whats-new' && <ChangelogList editable={isSuperAdmin} />}

      {activeTab === 'documentation' && <div className="space-y-4">
        {/* ──────────── Architecture Overview ──────────── */}
        <Section
          id="architecture"
          icon={<Server className="w-5 h-5" />}
          title="Architecture Overview"
          description="High-level system design and data flow"
          defaultOpen
        >
          <div className="space-y-4">
            <p className="text-sm text-foreground">
              The platform follows a classic <strong>monorepo</strong> structure with a React&nbsp;19 SPA frontend and an
              Express.js REST API backend, sharing a PostgreSQL database with pgvector for AI embeddings.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="p-4 bg-primary-50 rounded-lg border border-primary-100">
                <div className="flex items-center gap-2 mb-2">
                  <Globe className="w-4 h-4 text-primary-600" />
                  <h4 className="font-semibold text-sm text-foreground">Frontend</h4>
                </div>
                <p className="text-xs text-muted-foreground">React 19 + TypeScript 5 + Vite 7 + TanStack Router & Query + Tailwind CSS</p>
              </div>
              <div className="p-4 bg-success-50 rounded-lg border border-success-100">
                <div className="flex items-center gap-2 mb-2">
                  <Cpu className="w-4 h-4 text-success-600" />
                  <h4 className="font-semibold text-sm text-foreground">Backend</h4>
                </div>
                <p className="text-xs text-muted-foreground">Node.js 22 + Express 5 + Knex.js + Zod validation + JWT auth</p>
              </div>
              <div className="p-4 bg-secondary-50 rounded-lg border border-secondary-100">
                <div className="flex items-center gap-2 mb-2">
                  <Database className="w-4 h-4 text-secondary-600" />
                  <h4 className="font-semibold text-sm text-foreground">Database</h4>
                </div>
                <p className="text-xs text-muted-foreground">PostgreSQL 15 with pgvector extension via Docker Compose</p>
              </div>
            </div>

            <div className="bg-muted rounded-lg p-4 text-xs text-foreground font-mono leading-relaxed">
              Browser → Nginx (SSL) → Vite Dev Server (static) + Express API (:3021)<br />
              Express → PostgreSQL (pgvector) + Cloudflare R2 (files) + Brevo (email)<br />
              Express → OpenAI / Google Gemini (LLM) + Sentry (errors)
            </div>
          </div>
        </Section>

        {/* ──────────── External Service Dependencies ──────────── */}
        <Section
          id="dependencies"
          icon={<Cloud className="w-5 h-5" />}
          title="External Service Dependencies"
          description="Third-party services the platform relies on"
        >
          <div className="space-y-6">
            {/* PostgreSQL */}
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Database className="w-4 h-4 text-blue-600" />
                <h4 className="font-semibold text-sm">PostgreSQL 15 + pgvector</h4>
                <Badge variant="primary">Required</Badge>
              </div>
              <p className="text-sm text-muted-foreground">
                Primary data store. Runs via Docker Compose using the <code className="text-xs bg-muted px-1 rounded">pgvector/pgvector:pg15</code> image.
                The pgvector extension enables AI embedding storage and vector similarity search for the knowledge base.
              </p>
              <div className="bg-muted rounded p-3">
                <EnvVar name="DB_HOST" example="localhost" description="Database hostname" />
                <EnvVar name="DB_PORT" example="5434" description="Host port mapped to container's 5432" />
                <EnvVar name="DB_NAME" example="rocket_routine_db" description="Database name" />
                <EnvVar name="DB_USER" description="Database user" />
                <EnvVar name="DB_PASSWORD" description="Database password" />
              </div>
            </div>

            {/* R2 */}
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <HardDrive className="w-4 h-4 text-orange-600" />
                <h4 className="font-semibold text-sm">Cloudflare R2 (S3-Compatible Storage)</h4>
                <Badge variant="warning">Required for file uploads</Badge>
              </div>
              <p className="text-sm text-muted-foreground">
                Used for all file uploads — attachments, documents, and organization assets. R2 is S3-compatible,
                so any S3-compatible provider (AWS S3, MinIO, Backblaze B2) can be swapped in by changing these env vars.
              </p>
              <div className="bg-muted rounded p-3">
                <EnvVar name="R2_ACCOUNT_ID" description="Cloudflare account ID" />
                <EnvVar name="R2_ACCESS_KEY_ID" description="R2 access key" />
                <EnvVar name="R2_SECRET_ACCESS_KEY" description="R2 secret key" />
                <EnvVar name="R2_BUCKET_NAME" description="Bucket name for file storage" />
                <EnvVar name="R2_PUBLIC_URL" description="Public URL prefix for serving uploaded files" />
              </div>
              <div className="flex items-start gap-2 p-3 bg-amber-50 rounded border border-amber-200">
                <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
                <p className="text-xs text-amber-800">
                  Without R2 configured, file upload features will fail. The platform will still function for all non-file features.
                  If you don't need file uploads, you can leave these empty — the upload endpoints will return errors but nothing else breaks.
                </p>
              </div>
            </div>

            {/* Brevo */}
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-green-600" />
                <h4 className="font-semibold text-sm">Brevo (Transactional Email)</h4>
                <Badge variant="warning">Required for email features</Badge>
              </div>
              <p className="text-sm text-muted-foreground">
                Handles all outbound email — account verification, password resets, team invitations, and notification emails.
                Uses Brevo's REST API directly (no SDK), so it's straightforward to swap for SendGrid, Postmark, or any transactional email provider
                by modifying <code className="text-xs bg-muted px-1 rounded">server/lib/email/</code>.
              </p>
              <div className="bg-muted rounded p-3">
                <EnvVar name="BREVO_API_KEY" description="Brevo API key for sending transactional email" />
                <EnvVar name="EMAIL_FROM" example="noreply@yourdomain.com" description="Sender email address" />
                <EnvVar name="EMAIL_FROM_NAME" example="Your App" description="Sender display name" />
              </div>
              <div className="flex items-start gap-2 p-3 bg-amber-50 rounded border border-amber-200">
                <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
                <p className="text-xs text-amber-800">
                  Without Brevo configured, password resets, email verification, and team invitations will fail silently.
                  Users can still register and log in, but email-dependent flows won't work.
                </p>
              </div>
            </div>

            {/* LLM */}
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Brain className="w-4 h-4 text-purple-600" />
                <h4 className="font-semibold text-sm">OpenAI + Google Gemini (LLM)</h4>
                <Badge variant="outline">Optional</Badge>
              </div>
              <p className="text-sm text-muted-foreground">
                Powers AI features — content generation, knowledge base embeddings, website profile analysis.
                The LLM service abstracts both providers behind a unified interface. Configure one or both.
              </p>
              <div className="bg-muted rounded p-3">
                <EnvVar name="OPENAI_API_KEY" description="OpenAI API key (for GPT models and embeddings)" />
                <EnvVar name="GEMINI_API_KEY" description="Google Gemini API key (for Gemini models)" />
              </div>
            </div>

            {/* Sentry */}
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-red-600" />
                <h4 className="font-semibold text-sm">Sentry (Error Tracking)</h4>
                <Badge variant="outline">Optional</Badge>
              </div>
              <p className="text-sm text-muted-foreground">
                Frontend and backend error tracking. Captures unhandled exceptions and performance data.
              </p>
              <div className="bg-muted rounded p-3">
                <EnvVar name="VITE_SENTRY_DSN" description="Sentry DSN for the frontend React app" />
                <EnvVar name="SENTRY_DSN" description="Sentry DSN for the backend Express server" />
              </div>
            </div>
          </div>
        </Section>

        {/* ──────────── Project Structure ──────────── */}
        <Section
          id="structure"
          icon={<FolderTree className="w-5 h-5" />}
          title="Project Structure"
          description="Key directories and their purpose"
        >
          <CodeBlock>{`├── src/                        # Frontend (React 19 + TypeScript)
│   ├── components/ui/          # Reusable UI component library
│   ├── hooks/                  # Custom React hooks (queries, mutations)
│   ├── lib/                    # Utilities (api client, date formatters)
│   ├── routes/                 # Page components (TanStack Router)
│   │   ├── admin/              # Super-admin pages
│   │   ├── organization/       # Org-scoped pages
│   │   └── settings/           # User settings
│   ├── services/               # API service classes
│   └── stores/                 # Zustand state stores
│
├── server/                     # Backend (Express 5 + TypeScript)
│   ├── db/                     # Knex config, connection
│   ├── http/                   # Express routes & middleware
│   │   ├── routes/             # Route handlers
│   │   └── middleware/         # Auth, RBAC, error handling
│   ├── lib/                    # Shared libs (email, LLM, storage)
│   │   ├── email/              # Brevo email integration
│   │   ├── llm/                # LLM abstraction (OpenAI + Gemini)
│   │   └── storage/            # R2/S3 file storage
│   ├── migrations/             # Knex database migrations
│   ├── modules/                # Domain modules (services, models)
│   └── seeds/                  # Database seed data
│
├── docker-compose.yml          # PostgreSQL + pgvector container
├── .env                        # Environment configuration
└── vite.config.ts              # Vite frontend build config`}</CodeBlock>
        </Section>

        {/* ──────────── Quick Start ──────────── */}
        <Section
          id="quickstart"
          icon={<Terminal className="w-5 h-5" />}
          title="Quick Start Guide"
          description="Get the platform running locally"
        >
          <div className="space-y-5">
            <div className="flex gap-4">
              <div className="flex-shrink-0 w-8 h-8 bg-primary-100 text-primary-700 rounded-full flex items-center justify-center font-semibold text-sm">1</div>
              <div className="flex-1 min-w-0">
                <h4 className="font-medium text-foreground mb-1">Clone & install</h4>
                <CodeBlock>{`git clone <repo-url> && cd rocket-routine-os
npm install`}</CodeBlock>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex-shrink-0 w-8 h-8 bg-primary-100 text-primary-700 rounded-full flex items-center justify-center font-semibold text-sm">2</div>
              <div className="flex-1 min-w-0">
                <h4 className="font-medium text-foreground mb-1">Start PostgreSQL</h4>
                <CodeBlock>{`docker compose up -d`}</CodeBlock>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex-shrink-0 w-8 h-8 bg-primary-100 text-primary-700 rounded-full flex items-center justify-center font-semibold text-sm">3</div>
              <div className="flex-1 min-w-0">
                <h4 className="font-medium text-foreground mb-1">Configure environment</h4>
                <CodeBlock>{`cp env.example .env
# Edit .env with your database credentials and API keys`}</CodeBlock>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex-shrink-0 w-8 h-8 bg-primary-100 text-primary-700 rounded-full flex items-center justify-center font-semibold text-sm">4</div>
              <div className="flex-1 min-w-0">
                <h4 className="font-medium text-foreground mb-1">Run migrations & seeds</h4>
                <CodeBlock>{`npm run db:migrate
npm run db:seed`}</CodeBlock>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex-shrink-0 w-8 h-8 bg-primary-100 text-primary-700 rounded-full flex items-center justify-center font-semibold text-sm">5</div>
              <div className="flex-1 min-w-0">
                <h4 className="font-medium text-foreground mb-1">Start development servers</h4>
                <CodeBlock>{`npm run dev          # Starts both frontend (Vite) and backend (Express)`}</CodeBlock>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex-shrink-0 w-8 h-8 bg-primary-100 text-primary-700 rounded-full flex items-center justify-center font-semibold text-sm">6</div>
              <div className="flex-1 min-w-0">
                <h4 className="font-medium text-foreground mb-1">Open the app</h4>
                <p className="text-sm text-muted-foreground">
                  Visit <code className="text-xs bg-muted px-1 rounded">http://localhost:3022</code> — the seed creates a default admin user.
                </p>
              </div>
            </div>
          </div>
        </Section>

        {/* ──────────── Authentication & Auth ──────────── */}
        <Section
          id="auth"
          icon={<Lock className="w-5 h-5" />}
          title="Authentication & Authorization"
          description="JWT auth, refresh tokens, and role-based access"
        >
          <div className="space-y-4">
            <div>
              <h4 className="font-semibold text-sm text-foreground mb-2">Authentication Flow</h4>
              <ul className="text-sm text-muted-foreground space-y-1 list-disc list-inside">
                <li>JWT access tokens (short-lived, ~15 min) stored in memory</li>
                <li>Refresh tokens (long-lived, ~7 days) stored in HTTP-only cookies</li>
                <li>Automatic token refresh on 401 responses via API interceptor</li>
                <li>Email verification required for new accounts (when Brevo is configured)</li>
                <li>Password reset via email link</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-sm text-foreground mb-2">RBAC System</h4>
              <ul className="text-sm text-muted-foreground space-y-1 list-disc list-inside">
                <li><strong>Super Admin</strong> — platform-wide access, system settings, all organizations</li>
                <li><strong>Org Admin</strong> — full access within their organization</li>
                <li><strong>Custom Roles</strong> — configurable permission sets per organization</li>
                <li>Permissions are checked via <code className="text-xs bg-muted px-1 rounded">requirePermission()</code> middleware on the backend
                  and <code className="text-xs bg-muted px-1 rounded">usePermissions()</code> hook on the frontend</li>
              </ul>
            </div>
            <div className="bg-muted rounded p-3">
              <EnvVar name="JWT_SECRET" description="Secret key for signing JWT tokens" />
              <EnvVar name="JWT_REFRESH_SECRET" description="Separate secret for refresh tokens" />
            </div>
          </div>
        </Section>

        {/* ──────────── Background Jobs ──────────── */}
        <Section
          id="jobs"
          icon={<Clock className="w-5 h-5" />}
          title="Background Jobs System"
          description="Scheduled and on-demand job processing"
        >
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              The platform includes a built-in job queue backed by the <code className="text-xs bg-muted px-1 rounded">scheduled_jobs</code> database table.
              Jobs can be triggered on a schedule (cron-style) or on demand via API.
            </p>
            <ul className="text-sm text-muted-foreground space-y-1 list-disc list-inside">
              <li>Job definitions live in <code className="text-xs bg-muted px-1 rounded">server/modules/scheduler/</code></li>
              <li>Each job has a type, status, next run time, and execution log</li>
              <li>Failed jobs are retried with configurable backoff</li>
              <li>Super admins can view and manage jobs from the Admin → Scheduled Jobs page</li>
            </ul>
          </div>
        </Section>

        {/* ──────────── All Features ──────────── */}
        <Section
          id="features"
          icon={<CheckCircle className="w-5 h-5" />}
          title="Included Features"
          description="Everything that ships out of the box"
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <FeatureItem icon={<Users className="w-5 h-5" />} title="User Management" description="Registration, login, password reset, profile editing" />
            <FeatureItem icon={<Shield className="w-5 h-5" />} title="RBAC Permissions" description="Granular role-based access control with custom roles" />
            <FeatureItem icon={<Users className="w-5 h-5" />} title="Multi-Tenancy" description="Organizations with members, invites, and org switching" />
            <FeatureItem icon={<Upload className="w-5 h-5" />} title="File Uploads" description="R2/S3 file storage with per-org scoping" />
            <FeatureItem icon={<Sparkles className="w-5 h-5" />} title="AI / LLM Service" description="Unified OpenAI + Gemini with usage tracking" />
            <FeatureItem icon={<Database className="w-5 h-5" />} title="Knowledge Base" description="Vector embeddings for org knowledge with search" />
            <FeatureItem icon={<Mail className="w-5 h-5" />} title="Transactional Email" description="Brevo-powered verification, reset, invitation emails" />
            <FeatureItem icon={<Clock className="w-5 h-5" />} title="Job Queue" description="Scheduled & on-demand background job processing" />
            <FeatureItem icon={<BarChart3 className="w-5 h-5" />} title="AI Usage Analytics" description="Token usage tracking and cost analysis dashboard" />
            <FeatureItem icon={<Bell className="w-5 h-5" />} title="Notifications" description="In-app toast notifications with notification store" />
            <FeatureItem icon={<Palette className="w-5 h-5" />} title="UI Component Library" description="40+ reusable components with consistent design system" />
            <FeatureItem icon={<Settings className="w-5 h-5" />} title="Settings System" description="App-level and org-level configurable settings" />
          </div>
        </Section>

        {/* ──────────── Environment Variables ──────────── */}
        <Section
          id="env"
          icon={<Key className="w-5 h-5" />}
          title="Environment Variables Reference"
          description="Complete list of all configuration options"
        >
          <div className="space-y-6">
            <div>
              <h4 className="font-semibold text-sm text-foreground mb-2">Application</h4>
              <div className="bg-muted rounded p-3">
                <EnvVar name="PORT" example="3021" description="Backend API server port" />
                <EnvVar name="NODE_ENV" example="development" description="Environment mode" />
                <EnvVar name="APP_URL" example="http://localhost:3022" description="Full public URL of the application" />
                <EnvVar name="VITE_API_URL" example="http://localhost:3021" description="API URL used by the frontend" />
              </div>
            </div>

            <div>
              <h4 className="font-semibold text-sm text-foreground mb-2">Database</h4>
              <div className="bg-muted rounded p-3">
                <EnvVar name="DB_HOST" example="localhost" description="PostgreSQL host" />
                <EnvVar name="DB_PORT" example="5434" description="PostgreSQL port" />
                <EnvVar name="DB_NAME" example="rocket_routine_db" description="Database name" />
                <EnvVar name="DB_USER" description="Database user" />
                <EnvVar name="DB_PASSWORD" description="Database password" />
              </div>
            </div>

            <div>
              <h4 className="font-semibold text-sm text-foreground mb-2">Authentication</h4>
              <div className="bg-muted rounded p-3">
                <EnvVar name="JWT_SECRET" description="JWT access token signing secret" />
                <EnvVar name="JWT_REFRESH_SECRET" description="JWT refresh token signing secret" />
              </div>
            </div>

            <div>
              <h4 className="font-semibold text-sm text-foreground mb-2">File Storage (R2 / S3)</h4>
              <div className="bg-muted rounded p-3">
                <EnvVar name="R2_ACCOUNT_ID" description="Cloudflare account ID" />
                <EnvVar name="R2_ACCESS_KEY_ID" description="S3-compatible access key" />
                <EnvVar name="R2_SECRET_ACCESS_KEY" description="S3-compatible secret key" />
                <EnvVar name="R2_BUCKET_NAME" description="Storage bucket name" />
                <EnvVar name="R2_PUBLIC_URL" description="Public URL for serving files" />
              </div>
            </div>

            <div>
              <h4 className="font-semibold text-sm text-foreground mb-2">Email (Brevo)</h4>
              <div className="bg-muted rounded p-3">
                <EnvVar name="BREVO_API_KEY" description="Brevo API key" />
                <EnvVar name="EMAIL_FROM" example="noreply@example.com" description="Sender email" />
                <EnvVar name="EMAIL_FROM_NAME" example="SaaS App" description="Sender name" />
              </div>
            </div>

            <div>
              <h4 className="font-semibold text-sm text-foreground mb-2">AI / LLM</h4>
              <div className="bg-muted rounded p-3">
                <EnvVar name="OPENAI_API_KEY" description="OpenAI API key" />
                <EnvVar name="GEMINI_API_KEY" description="Google Gemini API key" />
              </div>
            </div>

            <div>
              <h4 className="font-semibold text-sm text-foreground mb-2">Error Tracking</h4>
              <div className="bg-muted rounded p-3">
                <EnvVar name="SENTRY_DSN" description="Backend Sentry DSN" />
                <EnvVar name="VITE_SENTRY_DSN" description="Frontend Sentry DSN" />
              </div>
            </div>
          </div>
        </Section>

        {/* ──────────── Common Commands ──────────── */}
        <Section
          id="commands"
          icon={<Terminal className="w-5 h-5" />}
          title="Common Commands"
          description="Useful development and management commands"
        >
          <div className="space-y-4">
            <div>
              <h4 className="text-sm font-medium text-foreground mb-2">Development</h4>
              <CodeBlock>{`npm run dev              # Start both frontend + backend
npm run dev:server       # Backend only
npm run dev:client       # Frontend only (Vite)`}</CodeBlock>
            </div>
            <div>
              <h4 className="text-sm font-medium text-foreground mb-2">Database</h4>
              <CodeBlock>{`npm run db:migrate       # Run pending migrations
npm run db:rollback      # Rollback last migration batch
npm run db:seed          # Seed database with initial data
npm run db:reset         # Rollback all + migrate + seed`}</CodeBlock>
            </div>
            <div>
              <h4 className="text-sm font-medium text-foreground mb-2">Docker</h4>
              <CodeBlock>{`docker compose up -d     # Start PostgreSQL container
docker compose down      # Stop PostgreSQL container
docker compose logs -f   # View container logs`}</CodeBlock>
            </div>
            <div>
              <h4 className="text-sm font-medium text-foreground mb-2">Production (PM2)</h4>
              <CodeBlock>{`pm2 start ecosystem.config.js   # Start all services
pm2 restart all                 # Restart all services
pm2 logs                        # View all logs
pm2 status                      # Service status`}</CodeBlock>
            </div>
          </div>
        </Section>

        {/* ──────────── Important Notes ──────────── */}
        <Section
          id="notes"
          icon={<AlertTriangle className="w-5 h-5" />}
          title="Important Notes"
          description="Things to know before deploying"
        >
          <div className="space-y-3">
            <div className="flex items-start gap-2 p-3 bg-blue-50 rounded border border-blue-200">
              <Code className="w-4 h-4 text-blue-600 mt-0.5 shrink-0" />
              <p className="text-xs text-blue-800">
                <strong>Node.js 22+</strong> is required — Vite 7 and other dependencies need it.
              </p>
            </div>
            <div className="flex items-start gap-2 p-3 bg-blue-50 rounded border border-blue-200">
              <Database className="w-4 h-4 text-blue-600 mt-0.5 shrink-0" />
              <p className="text-xs text-blue-800">
                <strong>pgvector extension</strong> is required for the knowledge base. The Docker Compose file uses the
                pgvector-enabled PostgreSQL image automatically.
              </p>
            </div>
            <div className="flex items-start gap-2 p-3 bg-amber-50 rounded border border-amber-200">
              <Lock className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
              <p className="text-xs text-amber-800">
                <strong>Change JWT secrets</strong> before deploying to production. Use long, random strings (64+ characters).
              </p>
            </div>
            <div className="flex items-start gap-2 p-3 bg-amber-50 rounded border border-amber-200">
              <Mail className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
              <p className="text-xs text-amber-800">
                <strong>Email provider is swappable.</strong> Brevo is the default, but the email layer in
                <code className="text-xs bg-amber-100 px-1 rounded mx-1">server/lib/email/</code> uses plain HTTP calls —
                easy to replace with SendGrid, Postmark, AWS SES, or any provider.
              </p>
            </div>
            <div className="flex items-start gap-2 p-3 bg-amber-50 rounded border border-amber-200">
              <HardDrive className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
              <p className="text-xs text-amber-800">
                <strong>Storage provider is swappable.</strong> R2 is the default, but the storage layer uses the S3 SDK —
                switch to AWS S3, MinIO, or Backblaze B2 by changing env vars.
              </p>
            </div>
          </div>
        </Section>
      </div>}
    </div>
  );
}
